import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AttentionStrip } from './AttentionStrip'
import { useRegisterHeader, useShell } from './shell'

export interface PageHeaderProps {
  title: ReactNode
  /** One line, only when the title alone does not say what the page is for. */
  description?: ReactNode
  /** The page's one main action, on the right. */
  actions?: ReactNode
  /** A parent page: "리딤 › Korio". */
  parent?: { to: string; label: string }
  /** The filter row, under the title. */
  children?: ReactNode
  /** The browser tab's name when `title` is not plain text. */
  docTitle?: string
  /** `false` leaves the attention strip out — for a page that already lists
   *  the same things itself (오늘). */
  attention?: boolean
  className?: string
}

const APP_NAME = 'Viral 운영 콘솔'

/** "검토 · Viral 운영 콘솔" in the tab, so a row of console tabs can be told
 *  apart. */
function useDocumentTitle(name: string | undefined) {
  useEffect(() => {
    if (!name) return
    const before = document.title
    document.title = `${name} · ${APP_NAME}`
    return () => {
      document.title = before
    }
  }, [name])
}

/** The top of every page: title, an optional line, the main action, then the
 *  filters. The attention strip follows it on every page. */
export function PageHeader({ title, description, actions, parent, children, docTitle, attention = true, className }: PageHeaderProps) {
  useRegisterHeader()
  useDocumentTitle(docTitle ?? (typeof title === 'string' ? title : undefined))
  const inShell = !!useShell()
  return (
    <>
      <header className={cn('mb-4 space-y-3', className)}>
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            {parent && (
              <Link
                to={parent.to}
                className="mb-1 inline-flex items-center gap-0.5 text-meta text-muted-foreground hover:text-foreground"
              >
                {parent.label}
                <ChevronRight size={12} aria-hidden />
              </Link>
            )}
            <h1 className="text-title font-semibold">{title}</h1>
            {description && <p className="mt-1 text-body text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {children}
      </header>
      {inShell && attention && <AttentionStrip className="mb-4" />}
    </>
  )
}
